import { Button } from '@cbhq/cds-web/buttons/Button';
import { Chip } from '@cbhq/cds-web/chips';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { memo, useMemo } from 'react';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { TextLabel1 } from '@cbhq/cds-web/typography/TextLabel1';
import { RemoteImage } from '@cbhq/cds-web/media';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { TextLabel2 } from '@cbhq/cds-web/typography/TextLabel2';
import { TextBody, TextTitle3 } from '@cbhq/cds-web/typography';
import { SpotIcon } from '@cbhq/cds-web/illustrations/SpotIcon';
import { useChainId } from 'wagmi';
import { css } from '@emotion/css';
import { TagLabel } from '@/components/TagLabels/TagLabel';
import { USDCRewardsChip } from '@/components/common/USDCRewardsChip';
import { PoolsListTokensMedia } from '@/components/Pools/PoolsListTokensMedia';
import { useAddLiquidityState } from '@/components/AddLiquidity/AddLiquidityProvider';
import { useNetworkCost } from '@/onchain/hooks/useNetworkCost';
import { format, DisplayType, formatTickPrice } from '@/utils/numberFormatter/formatNumbers';
import { Bound } from '@/utils/types';
import { isToken0PreferredQuoteToken } from '@/utils/tokenPreference';
import {
  NESTED_CARD_BORDER_RADIUS,
  NESTED_CARD_PADDING,
  NESTED_CARD_GAP,
  CARD_GAP,
} from '@/constants/layout';
import { usePoolsInfo } from '@/components/Pools/hooks/usePoolsInfo';
import { useTokenPrices } from '@/hooks/useTokenPrice';

const ESTIMATED_GAS_LIMIT_NEW_POSITION = 600_000n;
const ESTIMATED_GAS_LIMIT_ADD_LIQUIDITY = 300_000n;

// Match the 28px font-size used in AddLiquidityDepositInputs and AddLiquidityPriceInputs
const ValueTextStyles = css`
  font-weight: var(--fontWeight-body);
  font-family: var(--fontFamily-body);
  font-size: 28px;
  line-height: 36px;
  color: var(--color-fg);
  margin: 0;
  white-space: nowrap;
`;

// TODO: Replace after merging PR [https://github.cbhq.net/c3/clear-pools-ui/pull/265]
const numberFormatters = {
  percentFee: new Intl.NumberFormat('en', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 6,
  }),
  apr: new Intl.NumberFormat('en', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }),
};

type AddLiquidityReviewContentProps = {
  onBack: () => void;
  onContinue: () => void;
};

/**
 * Review content for add liquidity flow.
 * Displays token pair summary, deposit amounts, price range, and network cost.
 *
 * This component is used within the AddLiquidity review step and preserves
 * all existing testIDs for E2E compatibility.
 */
export const AddLiquidityReviewContent = memo(function AddLiquidityReviewContent({
  onBack,
  onContinue,
}: AddLiquidityReviewContentProps) {
  const {
    positionId,
    positionTokens,
    tickInRange,
    feePercent,
    depositAmountsInput,
    priceInputs,
    token0AsBaseToken,
    tickAtLimit,
    poolId,
  } = useAddLiquidityState();

  const chainId = useChainId();

  const networkCost = useNetworkCost(
    positionId ? ESTIMATED_GAS_LIMIT_ADD_LIQUIDITY : ESTIMATED_GAS_LIMIT_NEW_POSITION,
  );

  // Fetch APR data
  const { data: poolsData } = usePoolsInfo({ chainId, poolId: poolId ?? undefined });
  const apr7dPercentage = useMemo(() => {
    if (!poolId || !poolsData?.poolsInfo) return null;
    const pool = poolsData.poolsInfo.find((p) => p.poolId === poolId);
    return pool?.apr7dPercentage ?? null;
  }, [poolId, poolsData?.poolsInfo]);

  // Fetch USD prices for tokens
  const tokenSymbols = useMemo(
    () => [positionTokens[0].symbol, positionTokens[1].symbol],
    [positionTokens],
  );
  const { tokenPrices } = useTokenPrices(tokenSymbols);

  // Determine token ordering based on preference
  const token0AsPreferredQuoteToken = isToken0PreferredQuoteToken(
    positionTokens[0].symbol,
    positionTokens[1].symbol,
  );
  const [orderedBaseToken, orderedQuoteToken] = useMemo(() => {
    return token0AsPreferredQuoteToken
      ? [positionTokens[1], positionTokens[0]]
      : [positionTokens[0], positionTokens[1]];
  }, [positionTokens, token0AsPreferredQuoteToken]);

  // Calculate deposit data with USD values
  const tokenDeposits = useMemo(() => {
    return positionTokens
      .map(({ imageUrl, symbol }, i) => {
        const amount = depositAmountsInput[i];
        const price = tokenPrices[symbol] ?? 0;
        const usdValue = parseFloat(amount ?? '0') * price;
        return {
          imageUrl,
          symbol,
          amount,
          usdValue,
        };
      })
      .reverse();
  }, [positionTokens, depositAmountsInput, tokenPrices]);

  // Calculate total deposit USD value
  const totalDepositUSD = useMemo(() => {
    return tokenDeposits.reduce((acc, { usdValue }) => acc + usdValue, 0);
  }, [tokenDeposits]);

  const tokenPairLabel = useMemo(
    () =>
      token0AsBaseToken
        ? `${positionTokens[1].symbol} per ${positionTokens[0].symbol}`
        : `${positionTokens[0].symbol} per ${positionTokens[1].symbol}`,
    [token0AsBaseToken, positionTokens],
  );

  // Cast tickAtLimit to the expected type - provider may return numbers when isFullRange
  // but the logic works correctly since non-zero numbers are truthy
  const tickAtLimitBool = tickAtLimit as { [bound in Bound]?: boolean };

  const minMaxCards = useMemo(
    () => [
      {
        label: 'Lowest price',
        price: formatTickPrice({
          price: priceInputs.lower.value,
          atLimit: tickAtLimitBool,
          direction: Bound.LOWER,
        }),
      },
      {
        label: 'Highest price',
        price: formatTickPrice({
          price: priceInputs.upper.value,
          atLimit: tickAtLimitBool,
          direction: Bound.UPPER,
        }),
      },
    ],
    [priceInputs.lower.value, priceInputs.upper.value, tickAtLimitBool],
  );

  // Check if this is a USDC token pair (for showing USDCRewardsChip)
  const isUSDCTokenPair = positionTokens.some((token) => token.symbol === 'USDC');

  // Memoize assets array for PoolsListTokensMedia
  const tokenAssets = useMemo(
    () => [orderedBaseToken.asset, orderedQuoteToken.asset],
    [orderedBaseToken.asset, orderedQuoteToken.asset],
  );

  return (
    <VStack gap={CARD_GAP}>
      {/* Token pair header card */}
      <VStack
        padding={NESTED_CARD_PADDING}
        background="bgAlternate"
        borderRadius={NESTED_CARD_BORDER_RADIUS}
      >
        {/* Header content: Left side (icons, name, tags) and right side (In range - vertically centered) */}
        <HStack justifyContent="space-between" alignItems="center" gap={1}>
          {/* Left side: Token icons, name, and tags stacked vertically */}
          <VStack gap={NESTED_CARD_GAP} alignItems="flex-start">
            <HStack gap={1} alignItems="center">
              <PoolsListTokensMedia assets={tokenAssets} size="xl" />

              <TextTitle3 as="p" noWrap>
                <TextTitle3 as="span" testID="add-liquidity-review-token1-symbol">
                  {orderedBaseToken.symbol}
                </TextTitle3>
                {' / '}
                <TextTitle3 as="span" testID="add-liquidity-review-token0-symbol">
                  {orderedQuoteToken.symbol}
                </TextTitle3>
              </TextTitle3>
            </HStack>

            {/* Tags row */}
            <HStack gap={0.5} alignItems="center">
              <TagLabel testID="add-liquidity-review-fee" colorScheme="gray">
                {numberFormatters.percentFee.format(feePercent)}
              </TagLabel>
              {apr7dPercentage !== null && (
                <TagLabel testID="add-liquidity-review-apr" colorScheme="gray">
                  {numberFormatters.apr.format(apr7dPercentage)} APR
                </TagLabel>
              )}
              {isUSDCTokenPair && <USDCRewardsChip />}
            </HStack>
          </VStack>

          {/* Right side: In range indicator (vertically centered) */}
          <HStack gap={0.5} alignItems="center">
            <Icon
              name="dot"
              size="xs"
              active
              color={tickInRange ? 'fgPositive' : 'fgNegative'}
              aria-label={tickInRange ? 'Position in range' : 'Position out of range'}
            />
            <TextBody as="span" color="fgMuted" testID="add-liquidity-review-range-status">
              {tickInRange ? 'In range' : 'Out of range'}
            </TextBody>
          </HStack>
        </HStack>
      </VStack>

      {/* Deposit section */}
      <VStack gap={NESTED_CARD_GAP}>
        {/* Section header */}
        <HStack justifyContent="space-between" alignItems="center">
          <TextLabel1 as="p">Deposit</TextLabel1>
          <TextLabel1 as="p" testID="add-liquidity-review-total-deposit">
            {format({ value: totalDepositUSD, type: DisplayType.Fiat })}
          </TextLabel1>
        </HStack>

        {/* Deposit amount cards */}
        <HStack gap={CARD_GAP} width="100%" maxWidth="100%">
          {tokenDeposits.map(({ imageUrl, symbol, amount, usdValue }) => (
            <VStack
              key={symbol}
              padding={NESTED_CARD_PADDING}
              gap={NESTED_CARD_GAP}
              flexBasis="50%"
              flexShrink={1}
              borderRadius={NESTED_CARD_BORDER_RADIUS}
              bordered
            >
              {/* Row 1: Large number on left, token chip on right */}
              <HStack justifyContent="space-between" alignItems="center">
                <p className={ValueTextStyles}>
                  {format({ value: amount, type: DisplayType.TokenAmount })}
                </p>
                <Chip
                  testID={`add-liquidity-review-${symbol.toLowerCase()}-symbol`}
                  compact
                  start={<RemoteImage source={imageUrl} shape="circle" size="s" />}
                >
                  {symbol}
                </Chip>
              </HStack>
              {/* Row 2: USD value */}
              <TextLabel2 as="p" color="fgMuted">
                {tokenPairLabel} · {format({ value: usdValue, type: DisplayType.Fiat })}
              </TextLabel2>
            </VStack>
          ))}
        </HStack>
      </VStack>

      {/* Range section */}
      <VStack gap={NESTED_CARD_GAP}>
        {/* Section header */}
        <VStack gap={0.5}>
          <TextLabel1 as="p">Range</TextLabel1>
          <TextLabel2 as="p" color="fgMuted">
            The current price is{' '}
            {format({ value: priceInputs.current.value, type: DisplayType.TokenPrice })}{' '}
            {tokenPairLabel}
          </TextLabel2>
        </VStack>

        {/* Min/Max price cards */}
        <HStack gap={CARD_GAP} width="100%" maxWidth="100%">
          {minMaxCards.map(({ label, price }) => (
            <VStack key={label} width="50%" flexGrow={1} flexShrink={1} gap={0.5}>
              {/* Label outside the bordered card */}
              <TextLabel1 as="p" paddingY={0.5}>
                {label}
              </TextLabel1>

              {/* Bordered card containing price and token pair label */}
              <VStack
                bordered
                borderRadius={NESTED_CARD_BORDER_RADIUS}
                padding={NESTED_CARD_PADDING}
                gap={NESTED_CARD_GAP}
              >
                <p className={ValueTextStyles}>{price}</p>
                <TextLabel2 as="p" color="fgMuted">
                  {tokenPairLabel}
                </TextLabel2>
              </VStack>
            </VStack>
          ))}
        </HStack>
      </VStack>

      {/* Network cost */}
      <HStack
        width="100%"
        background="bgAlternate"
        borderRadius={NESTED_CARD_BORDER_RADIUS}
        padding={NESTED_CARD_PADDING}
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <TextLabel1 as="span" color="fgMuted">
          Network cost
        </TextLabel1>
        <HStack alignItems="center" gap={0.5}>
          <SpotIcon name="base" dimension="24x24" scaleMultiplier={0.8} />
          <TextLabel2 as="span" testID="add-liquidity-review-network-cost">
            {networkCost}
          </TextLabel2>
        </HStack>
      </HStack>

      {/* Back / Continue buttons */}
      <HStack gap={CARD_GAP} width="100%">
        <Button
          as="button"
          variant="secondary"
          block
          onClick={onBack}
          testID="add-liquidity-review-back-button"
        >
          Back
        </Button>
        <Button
          as="button"
          testID="add-liquidity-confirm-button"
          variant="primary"
          block
          onClick={onContinue}
        >
          Continue
        </Button>
      </HStack>
    </VStack>
  );
});
